import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, X } from "lucide-react";
import { VAULT } from "../data/vaultStats";

// Campaign window: visible until the end of 30 September 2026 (UK time), then both
// the banner and the homepage promo disappear automatically. Tracks stay in the vault.
export const BIRTHDAY_ENDS = new Date("2026-09-30T23:59:59+01:00");
export const isBirthdayCampaignLive = (now = new Date()) => now <= BIRTHDAY_ENDS;

export const BIRTHDAY = {
  key: "stagvault_birthday_banner_v1",
  newTracks: 20,
  newPrompts: 280,
  verified200: VAULT.tracks === 200,
  href: "/collection/birthday?utm_source=site&utm_medium=banner&utm_campaign=founders_birthday",
};

export const BirthdayBanner = ({ compact = false }) => {
  const [open, setOpen] = useState(() => {
    try { return localStorage.getItem(BIRTHDAY.key) !== "dismissed"; } catch { return true; }
  });
  if (!open || !isBirthdayCampaignLive()) return null;
  const dismiss = () => { try { localStorage.setItem(BIRTHDAY.key, "dismissed"); } catch { /* noop */ } setOpen(false); };

  return (
    <section
      role="region"
      aria-label="Founder's Birthday Special announcement"
      className="relative overflow-hidden border-y border-purple-500/40 bg-[#0b0614]"
      data-testid="birthday-banner"
    >
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,rgba(168,85,247,0.28),transparent_55%),radial-gradient(ellipse_at_bottom_right,rgba(176,112,255,0.14),transparent_50%)]" />
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-30 [background-image:radial-gradient(rgba(255,255,255,0.35)_1px,transparent_1px)] [background-size:22px_22px] motion-safe:animate-[pulse_6s_ease-in-out_infinite]" />
      <div className={`relative mx-auto flex max-w-7xl flex-col gap-4 px-4 sm:px-6 md:flex-row md:items-center md:justify-between ${compact ? "py-4" : "py-6 md:py-7"}`}>
        <div className="min-w-0">
          <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-amber-200">
            <Sparkles size={13} aria-hidden="true" /> Latest Stag Vault Drop
          </div>
          <h2 className="mt-2 font-display text-xl font-black leading-tight text-white sm:text-2xl md:text-3xl" data-testid="birthday-banner-headline">
            {BIRTHDAY.newTracks} brand-new income tracks have just landed
            {BIRTHDAY.verified200 && <span className="block text-purple-300">Celebrating {VAULT.tracks} Stag Vault tracks</span>}
          </h2>
          <p className="mt-2 text-sm text-zinc-300 sm:text-base" data-testid="birthday-banner-subline">
            {BIRTHDAY.newPrompts}+ new actionable AI prompts, included automatically with your existing Stag Vault access.
            <span className="hidden sm:inline"> Twenty new tracks. More practical paths to build and test.</span>
          </p>
        </div>
        <div className="flex items-center gap-3 md:shrink-0">
          <Link
            to={BIRTHDAY.href}
            className="inline-flex min-h-11 items-center gap-2 bg-purple-500 px-5 text-xs font-black uppercase tracking-[0.18em] text-white md:text-black transition-[background-color,transform] hover:bg-purple-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-amber-300"
            data-testid="birthday-banner-cta"
          >
            Explore the {BIRTHDAY.verified200 ? "20 new" : "new"} tracks <ArrowRight size={14} aria-hidden="true" />
          </Link>
          <button
            type="button"
            onClick={dismiss}
            aria-label="Dismiss the Founder's Birthday announcement"
            className="inline-flex h-11 w-11 items-center justify-center border border-zinc-700 text-zinc-400 transition-colors hover:border-purple-400 hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-amber-300"
            data-testid="birthday-banner-dismiss"
          >
            <X size={16} aria-hidden="true" />
          </button>
        </div>
      </div>
    </section>
  );
};

export const BirthdayPromoSection = () => isBirthdayCampaignLive() && (
  <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 md:px-10" data-testid="birthday-promo-section" aria-labelledby="birthday-promo-heading">
    <div className="grid gap-8 border border-purple-500/30 bg-purple-500/[0.04] p-6 md:grid-cols-12 md:p-10">
      <div className="md:col-span-7">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-200">Founder&apos;s Birthday Special</div>
        <h2 id="birthday-promo-heading" className="mt-3 font-display text-3xl font-black leading-tight text-white sm:text-4xl">
          {BIRTHDAY.newTracks} new tracks. {BIRTHDAY.newPrompts}+ new prompts.{BIRTHDAY.verified200 ? ` ${VAULT.tracks} tracks in the vault.` : ""}
        </h2>
        <p className="mt-4 max-w-2xl text-base leading-relaxed text-zinc-300" data-testid="birthday-promo-copy">
          Today is the Stag Vault Founder&apos;s birthday — but you&apos;re getting the presents. We&apos;ve added {BIRTHDAY.newTracks} brand-new income tracks and {BIRTHDAY.newPrompts} actionable AI prompts{BIRTHDAY.verified200 ? `, taking Stag Vault to ${VAULT.tracks} tracks` : ""}. Holiday-let compliance binders, farm diversification reports, pharmacy service promotion, livery-yard ops, sale-readiness data rooms, recurring online-presence monitoring and more — every one included with your existing access at no extra charge. Pick one, validate it and start building.
        </p>
      </div>
      <div className="flex flex-col justify-between gap-6 md:col-span-5">
        <ul className="grid grid-cols-3 gap-3 text-center">
          {[[BIRTHDAY.newTracks, "new tracks"], [`${BIRTHDAY.newPrompts}+`, "new prompts"], [VAULT.tracks, "tracks total"]].map(([v, l]) => (
            <li key={l} className="border border-zinc-800 bg-black/40 px-2 py-4"><div className="font-display text-2xl font-black text-white">{v}</div><div className="mt-1 text-[10px] uppercase tracking-[0.18em] text-zinc-500">{l}</div></li>
          ))}
        </ul>
        <Link to={BIRTHDAY.href} className="inline-flex min-h-12 items-center justify-center gap-2 bg-amber-300 px-6 text-sm font-black uppercase tracking-[0.14em] text-white md:text-black hover:bg-amber-200" data-testid="birthday-promo-cta">
          Explore the {BIRTHDAY.newTracks} new tracks <ArrowRight size={16} aria-hidden="true" />
        </Link>
      </div>
    </div>
  </section>
);
